import {
  type CertificateType,
  setIsCertificateTypesRequested,
} from '../../../Store/DataSlice'
import './CertificateTypeAdd.scss'
import { useEffect, useState } from 'react'
import { TextInput } from '../../../Components/TextInput/TextInput'
import { useLocalization } from '../../../Hooks/useLocalization'
import { Button } from '../../../Components/Button/Button'
import {useDispatch, useSelector} from 'react-redux'
import { setDestinationView } from '../../../Store/InterfaceSlice'
import { getAuthorizationHeader } from '../../../Orchestration/Authentication'
import { CheckboxOption } from '../../../Components/CheckboxOption/CheckboxOption'
import { getNaceSelectOptions } from '../../../Utils/NaceCodes'
import { MultiSelect } from '../../../Components/MultiSelect/MultiSelect'
import { LoadingSpinner } from '../../../Components/LoadingSpinner/LoadingSpinner'
import type {RootState} from "../../../Store/Store";

const emptyCertificateType: CertificateType = {
  name: '',
  isActive: true,
  naceLimitation: [],
  requiredCertificateNumbers: []
}

export function CertificateTypeAdd() {
  const dispatch = useDispatch()
  const localization = useLocalization('certificate-type')
  const [allNaceSelectOptions] = useState(getNaceSelectOptions())

  const allCertificateTypes = useSelector(
      (state: RootState) => state.data.certificateTypes
  )

  const [certificateType, setCertificateType] = useState<CertificateType>({
    ...emptyCertificateType,
  })

  const [validationErrors, setValidationErrors] = useState<validationError[]>(
    []
  )
  useEffect(() => {
    const newValidationErrors: validationError[] = []

    if (!certificateType.name)
      newValidationErrors.push({
        property: 'name',
        type: 'isRequired',
      })

    setValidationErrors(newValidationErrors)
  }, [certificateType])

  const [saveInProgress, setSaveInProgress] = useState(false)
  const saveCertificate = () => {
    setSaveInProgress(true)

    fetch(
      `https://qrzszdu3vd.execute-api.eu-west-1.amazonaws.com/default/certificate-type-add`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthorizationHeader(),
        },
        body: JSON.stringify({
          certificateType,
        }),
      }
    )
      .then(() => {
        dispatch(setIsCertificateTypesRequested(false))
        setTimeout(() => {
          dispatch(
            setDestinationView({ destinationView: 'certificate-type-list' })
          )
        }, 1)
      })
      .catch((err) => {
        console.warn(`fetch certificate-type-add failed: `, err)
      })
  }

  return (
    <div className="certificate-type-add">
      {saveInProgress ? (
        <div className="save-in-progress">
          <LoadingSpinner color="dark" />
          {localization.text(
            'certificate-type-save-in-progress',
            'Het certificaat type word momenteel opgeslagen.'
          )}
        </div>
      ) : (
        <>
          <TextInput
            label={localization.text('name', 'Naam')}
            isReadOnly={saveInProgress}
            onChange={(newValue) => {
              setCertificateType((previous) => ({
                ...previous,
                name: newValue,
              }))
            }}
            value={certificateType.name}
            isValid={!validationErrors.some((ve) => ve.property === 'name')}
            helperText={
              validationErrors.find((ve) => ve.property === 'name')?.type ===
              'isRequired'
                ? localization.text(
                    'name-missing',
                    'Geef een geldige certificaat type naam in'
                  )
                : undefined
            }
          />

          <CheckboxOption
            label={localization.text('isActive', 'Is actief')}
            isReadOnly={saveInProgress}
            onChange={(newValue) => {
              setCertificateType((previous) => ({
                ...previous,
                isActive: newValue,
              }))
            }}
            value={certificateType.isActive}
          />

          <MultiSelect
            label={localization.text(
              'naceLimitation',
              'Beperkt tot NACE codes'
            )}
            isReadOnly={saveInProgress}
            items={allNaceSelectOptions}
            onChange={(items) => {
              setCertificateType((p) => ({
                ...p,
                naceLimitation: items
              }))
            }}
            values={certificateType.naceLimitation}
          />

          <MultiSelect
              label={localization.text(
                  'requiredCertificateNumbers',
                  'Vereiste certificaat types'
              )}
              isReadOnly={saveInProgress}
              items={allCertificateTypes.map(type => { return { label: type.name, value: type.name} })}
              onChange={(items) => {
                setCertificateType((p) => ({
                  ...p,
                  requiredCertificateNumbers: items,
                }))
              }}
              values={certificateType.requiredCertificateNumbers}
          />

          <div className="buttons">
            <Button
              importance={'primary'}
              text={localization.text(
                'safe-add-button',
                'Voeg certificaat toe'
              )}
              onClick={saveCertificate}
              disabled={validationErrors.length > 0 || saveInProgress}
            />
          </div>
        </>
      )}
    </div>
  )
}

interface validationError {
  property: string
  type: 'isRequired' | 'format'
}
